import React, { FC } from 'react';
import { IconButton, useTheme } from '@mui/material';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { IEmployeeBalancesTransDTO } from 'src/api/dtos';
import { ArrowDownIcon, ArrowUpIcon, CloseIcon, Tooltip, WarningFilledIcon } from 'src/shared/components';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums, EmployeeBenefitStatusEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatDuration, formatTime, isAfterDay, isDateWithinDateRange, isSameDay } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';
import './AbsenceBalanceBreakdown.styles.scss';

interface Props {
  startDate: string;
  endDate: string;
  startTime: string | undefined;
  endTime: string | undefined;
  transactions: IEmployeeBalancesTransDTO[];
  status: EmployeeBenefitStatusEnums;
  className?: string;
}

export const AbsenceBalanceBreakdown: FC<Props> = ({
  startDate,
  endDate,
  startTime,
  endTime,
  transactions,
  status,
  className,
}) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);
  const theme = useTheme();

  return (
    <div className={className}>
      <table className={`absence-balances-breakdown ${theme.palette.mode}`}>
        <thead>
          <tr>
            <th scope="col">{t('global.date')}</th>
            <th scope="col">{t('global.amount')}</th>
            <th scope="col">{t('global.balance')}</th>
            <th scope="col">{t('global.action')}</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((item, index) => {
            const isSameStartDate = isSameDay(item.date, startDate);
            const isSameEndDate = isSameDay(item.date, endDate);
            const isBetweenSelectedDates = isDateWithinDateRange(item.date, { startDate, endDate });
            const isAfterEndDate = isAfterDay(item.date, endDate);
            const showTime = startTime && endTime && isSameStartDate;

            const highlightClass = isSameEndDate ? 'highlight' : '';
            const borderedTopClass = isSameStartDate ? 'highlight-border-top' : '';
            const borderedBottomClass = isSameEndDate ? 'highlight-border-bottom' : '';
            const borderedLeftClass = isBetweenSelectedDates ? 'highlight-border-left' : '';
            const borderedRightClass = isBetweenSelectedDates ? 'highlight-border-right' : '';
            const lowerOpacityClass = isAfterEndDate ? 'opacity-75' : '';
            const textDangerClass = item.hasError ? 'negative-balance' : '';
            const textMutedClass = isAfterEndDate ? 'text-muted' : '';

            const borderClasses = [borderedTopClass, borderedBottomClass].filter((c) => c.length > 0);
            const rowClasses = [lowerOpacityClass, textMutedClass].filter((c) => c.length > 0);

            const ErrorIcon = status === EmployeeBenefitStatusEnums.VIOLATED ? CloseIcon : WarningFilledIcon;
            const BalanceIcon = item.amountInSeconds < 0 ? ArrowDownIcon : ArrowUpIcon;

            return (
              <tr key={`breakdown-item-${index}`} className={rowClasses.join(' ')}>
                <td className={`${borderClasses.join(' ')} ${borderedLeftClass}`}>
                  {moment(item.date).format(DATE_FORMAT)}
                  {showTime && (
                    <span className="d-block small text-muted fw-400">
                      {formatTime(startTime, timeFormat, DATE_TIME_FORMAT)} -{' '}
                      {formatTime(endTime, timeFormat, DATE_TIME_FORMAT)}
                    </span>
                  )}
                </td>
                <td className={borderClasses.join(' ')}>
                  <BalanceIcon size={16} />
                  <span className="ms-1">
                    {formatDuration(Math.abs(item.amountInSeconds), DurationFormatEnums.T_24_HOURS, 'seconds')}
                  </span>
                </td>
                <td className={`${borderClasses.join(' ')} ${textDangerClass} ${highlightClass}`}>
                  {formatDuration(item.balanceInSeconds, DurationFormatEnums.T_24_HOURS, 'seconds')}
                  {item.errorMessage && (
                    <Tooltip title={item.errorMessage}>
                      <IconButton className="p-0 ms-1" size="small" color="inherit">
                        <ErrorIcon size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </td>
                <td className={`${borderClasses.join(' ')} ${borderedRightClass}`}>{item.actionName}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-2 mb-0 ms-2 small text-muted">
        {t('absences.showingCountRows', { count: transactions.length })}
      </p>
    </div>
  );
};
